import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import {
  OMNICHANNEL_MESSAGES_QUERY_KEY,
  omnichannelKeys,
} from '@/api/queries/omnichannel/key';
import { fetchLatestConversationMessagesPage } from '@/lib/omnichannel/fetch-latest-messages-page';
import { mergeOmnichannelMessageLists } from '@/lib/omnichannel/merge-messages';
import type { ChannelType, PaginatedMessagesResponse } from '@/types';

/**
 * Refreshes only the newest messages page and merges with cache so websocket
 * messages are not wiped by a stale REST response.
 */
export const refreshLatestOmnichannelMessagesCache = async (
  queryClient: QueryClient,
  params: {
    conversationId: string;
    channel: ChannelType;
    pageId?: string;
  }
): Promise<void> => {
  const { conversationId, channel, pageId } = params;
  const queryKey = omnichannelKeys.messages(conversationId, channel, pageId);
  const current =
    queryClient.getQueryData<InfiniteData<PaginatedMessagesResponse>>(queryKey);

  const latestPage = await fetchLatestConversationMessagesPage(conversationId, {
    channel,
    pageId,
  });

  if (!current?.pages?.length) {
    queryClient.setQueryData<InfiniteData<PaginatedMessagesResponse>>(
      queryKey,
      {
        pages: [latestPage],
        pageParams: [-1],
      }
    );
    return;
  }

  const lastIndex = current.pages.length - 1;
  const existingLatest = current.pages[lastIndex]?.data ?? [];
  const mergedLatest = mergeOmnichannelMessageLists(
    existingLatest,
    latestPage.data ?? []
  );

  queryClient.setQueryData<InfiniteData<PaginatedMessagesResponse>>(queryKey, {
    ...current,
    pages: current.pages.map((page, index) =>
      index === lastIndex ? { ...latestPage, data: mergedLatest } : page
    ),
  });
};

/** Refreshes the latest page for every cached thread variant of a conversation. */
export const refreshLatestOmnichannelMessagesCacheForConversation = async (
  queryClient: QueryClient,
  conversationId: string
): Promise<void> => {
  const queries = queryClient.getQueriesData<
    InfiniteData<PaginatedMessagesResponse>
  >({
    queryKey: [OMNICHANNEL_MESSAGES_QUERY_KEY, conversationId],
  });

  await Promise.all(
    queries.map(async ([queryKey]) => {
      const channel = queryKey[2] as ChannelType | undefined;
      if (!channel) return;
      const pageId = queryKey[3] as string | undefined;
      await refreshLatestOmnichannelMessagesCache(queryClient, {
        conversationId,
        channel,
        pageId,
      });
    })
  );
};
